/**
 * Item J on the WordPress reporter card: the backup and database block, the
 * "Varundus" tab and the update verdicts (docs/saidiseire-varundus.md).
 *
 * The server judges; this file only decides how its answer reads: which tone a
 * verdict wears, what a storage id or a schedule slug is called, how old a
 * backup is in words, and which one state the block's pill shows. Every
 * sentence is a catalogue key under `monitoring.reporter.backup` with its
 * values.
 *
 * React-free, tested in `monitoringBackup.test.ts` without mounting anything.
 * The types come from `monitoringReporter.ts` (type-only, so the two files do
 * not load each other).
 */
import type { StatusTone } from "../crm/ui";
import type {
  ReporterBackupSetView,
  ReporterBackupView,
  ReporterCleanupView,
  ReporterDatabaseView,
  ReporterExpectView,
  ReporterHistoryRow,
  ReporterReportView,
} from "./monitoringReporter";

const P = "monitoring.reporter.backup";

/** A catalogue key and the values its sentence prints. */
export interface BackupMessage {
  key: string;
  vars: Record<string, string | number>;
}

/** What a site without a package is asked for: nothing. */
export const NO_EXPECT: ReporterExpectView = { backupDaily: false, backupBeforeUpdate: false, dbCleanup: false };

/** The package asks for anything of the site's backups or database. */
export function expectsAny(expect: ReporterExpectView | null | undefined): boolean {
  return Boolean(expect && (expect.backupDaily || expect.backupBeforeUpdate || expect.dbCleanup));
}

// ── Update verdicts ──────────────────────────────────────────────────────────

/** `BackupBeforeView.verdict`: full | partial | stale | none | unknown. */
export const BACKUP_VERDICTS = ["full", "partial", "stale", "none", "unknown"] as const;
export type BackupVerdict = (typeof BACKUP_VERDICTS)[number];

/** A verdict this CRM knows; anything else (a newer server) reads as unknown. */
export function backupVerdict(verdict: string | null | undefined): BackupVerdict {
  return (BACKUP_VERDICTS as readonly string[]).includes(verdict ?? "") ? (verdict as BackupVerdict) : "unknown";
}

export function verdictTone(verdict: string): StatusTone {
  const v = backupVerdict(verdict);
  if (v === "full") return "ok";
  if (v === "partial") return "wt";
  if (v === "stale" || v === "none") return "lt";
  return "mut";
}

/** An update with no fresh backup at all is worth a look before anything else in the change list. */
export function verdictAlarm(verdict: string): boolean {
  const v = backupVerdict(verdict);
  return v === "stale" || v === "none";
}

/** The reports of the history that hold an update, with the verdict of its backups. */
export function updateRows(history: readonly ReporterHistoryRow[]): (ReporterHistoryRow & { backupBefore: string })[] {
  return history.filter((h): h is ReporterHistoryRow & { backupBefore: string } => h.backupBefore != null);
}

// ── The block's state ────────────────────────────────────────────────────────

/**
 * ok: UpdraftPlus makes backups and none is late for the package.
 * late: the package asks for daily backups and the newest database copy is older than 36 h.
 * none: no active backup plugin, or UpdraftPlus is on and has made no backup yet.
 * other: another backup plugin is active; its state is not read.
 * outdated: the package asks for something, and the report comes from a plugin before 1.1.0.
 * unknown: nothing to judge by and nothing asked.
 */
export type BackupState = "ok" | "late" | "none" | "other" | "outdated" | "unknown";

export function backupState(report: Pick<ReporterReportView, "backup">, expect: ReporterExpectView | null | undefined): BackupState {
  const b = report.backup ?? null;
  if (!b) return expectsAny(expect) ? "outdated" : "unknown";
  if (b.tool === "none") return "none";
  if (b.tool === "other") return "other";
  if (b.tool !== "updraftplus") return "unknown";
  if (b.sets.length === 0 && b.setCount === 0) return "none";
  // Without a package nothing is late: a weekly schedule is the site's own choice.
  if (expect?.backupDaily && b.dbOverdue) return "late";
  return "ok";
}

/** Missing backups are red only when the package asks for them; without a package they are a warning. */
export function backupStateTone(state: BackupState, expect: ReporterExpectView | null | undefined): StatusTone {
  if (state === "ok") return "ok";
  if (state === "late") return "lt";
  if (state === "none") return expect && (expect.backupDaily || expect.backupBeforeUpdate) ? "lt" : "wt";
  if (state === "other") return "info";
  if (state === "outdated") return "wt";
  return "mut";
}

/** The block is drawn when the report says anything about backups, or the package asks for them. */
export function showBackupBlock(report: Pick<ReporterReportView, "backup" | "database">, expect: ReporterExpectView | null | undefined): boolean {
  return report.backup != null || report.database != null || expectsAny(expect);
}

/** The muted line saying what the package asks for, or null when it asks nothing. */
export function expectKey(expect: ReporterExpectView | null | undefined): string | null {
  if (!expect) return null;
  if (expect.backupDaily && expect.dbCleanup) return `${P}.expect.dailyCleanup`;
  if (expect.backupDaily) return `${P}.expect.daily`;
  if (expect.dbCleanup) return `${P}.expect.cleanup`;
  if (expect.backupBeforeUpdate) return `${P}.expect.beforeUpdate`;
  return null;
}

// ── Words ────────────────────────────────────────────────────────────────────

/** UpdraftPlus's storage ids this CRM has a name for (`monitoring.reporter.backup.storage.<id>`). */
export const STORAGE_IDS = ["s3generic", "s3", "dropbox", "googledrive", "onedrive", "ftp", "sftp", "email", "none"] as const;

/** The catalogue key of a storage id, or null for one this CRM does not know (shown as the id itself). */
export function storageKey(id: string): string | null {
  return (STORAGE_IDS as readonly string[]).includes(id) ? `${P}.storage.${id}` : null;
}

/** Where new copies go: the configured storage, or "none" (the site's own server) when there is none. */
export function destinations(backup: Pick<ReporterBackupView, "services">): string[] {
  const out = backup.services.filter((s) => s && s !== "none");
  return out.length > 0 ? out : ["none"];
}

/** Where one set went: its storage, or "none" when it stayed on the site's server. */
export function setDestinations(set: Pick<ReporterBackupSetView, "remote" | "services">): string[] {
  const out = set.services.filter((s) => s && s !== "none");
  return set.remote && out.length > 0 ? out : ["none"];
}

/**
 * The schedule slugs this CRM has a word for: UpdraftPlus's intervals, and
 * WP-Optimize's (the same words with a `wpo_` prefix).
 */
export const SCHEDULES = [
  "manual",
  "everyhour",
  "every2hours",
  "every4hours",
  "every8hours",
  "twicedaily",
  "daily",
  "weekly",
  "fortnightly",
  "monthly",
] as const;

/** The catalogue key of a schedule slug (`monitoring.reporter.backup.every.<slug>`), or null for one this CRM does not know. */
export function scheduleKey(slug: string | null | undefined): string | null {
  if (!slug) return null;
  const bare = slug.startsWith("wpo_") ? slug.slice(4) : slug;
  return (SCHEDULES as readonly string[]).includes(bare) ? `${P}.every.${bare}` : null;
}

/** What one set holds: `monitoring.reporter.backup.holds.<both|db|files|other>`. */
export function setHoldsKey(set: Pick<ReporterBackupSetView, "db" | "files">): string {
  if (set.db && set.files) return `${P}.holds.both`;
  if (set.db) return `${P}.holds.db`;
  if (set.files) return `${P}.holds.files`;
  return `${P}.holds.other`;
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * How long ago, in the largest unit that still says something: minutes
 * under an hour, hours under two days, days after that. A time ahead of the
 * clock (the site's clock runs fast) reads as "just now".
 */
export function ageMessage(iso: string, now: number): BackupMessage {
  const ms = now - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < MINUTE_MS) return { key: `${P}.ago.justNow`, vars: {} };
  if (ms < HOUR_MS) return { key: `${P}.ago.minutes`, vars: { n: Math.floor(ms / MINUTE_MS) } };
  if (ms < 2 * DAY_MS) return { key: `${P}.ago.hours`, vars: { n: Math.floor(ms / HOUR_MS) } };
  return { key: `${P}.ago.days`, vars: { n: Math.floor(ms / DAY_MS) } };
}

// ── The database ─────────────────────────────────────────────────────────────

/** WordPress Site Health warns above this many bytes of autoloaded options; the API raises `db_autoload` above it. */
export const AUTOLOAD_WARN_BYTES = 800_000;

export function autoloadHeavy(bytes: number | null | undefined): boolean {
  return typeof bytes === "number" && bytes > AUTOLOAD_WARN_BYTES;
}

/**
 * WP-Optimize's scheduled clean-up.
 * unknown: the report carries no database part (a plugin before 1.1.0).
 * missing: WP-Optimize is not installed. inactive: installed, switched off.
 * off: on, but its schedule is off. on: it cleans on its own schedule.
 */
export type CleanupState = "unknown" | "missing" | "inactive" | "off" | "on";

export function cleanupState(db: ReporterDatabaseView | null | undefined): CleanupState {
  if (!db) return "unknown";
  const c: ReporterCleanupView | null = db.cleanup;
  if (!c) return "missing";
  if (!c.active) return "inactive";
  return c.scheduled ? "on" : "off";
}

/** The database counts the tab lists, in its order, as `monitoring.reporter.backup.db.<key>`. */
export const DB_COUNTS = [
  "revisions",
  "autoDrafts",
  "trashedPosts",
  "spamComments",
  "trashedComments",
  "expiredTransients",
] as const satisfies readonly (keyof ReporterDatabaseView)[];
